// components/cards/TeamSpendingCard.jsx - ENHANCED VERSION WITH TOOLTIPS
import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Tooltip,
  Skeleton,
  Chip,
  Alert,
} from "@mui/material";
import { useTheme, alpha } from '@mui/material/styles';
import {
  Warning as WarningIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  Info as InfoIcon,
  ErrorOutline as ErrorOutlineIcon,
  TrendingUp as TrendingUpIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  HelpOutline as HelpOutlineIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";

const TeamSpendingCard = ({ spendingInfo, onClick, loading = false }) => {
  const theme = useTheme();

  // Loading state
  if (loading) {
    return (
      <Card 
        elevation={0}
        sx={{ 
          p: 2.5, 
          borderRadius: 4, 
          height: '100%',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
          <Skeleton variant="circular" width={44} height={44} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={16} />
          </Box>
        </Box>
        <Skeleton variant="text" width="50%" height={36} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="70%" height={20} sx={{ mb: 1.5 }} />
        <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 2 }} />
      </Card>
    );
  }

  // No spending info state
  if (!spendingInfo) {
    return (
      <Card 
        elevation={0}
        sx={{ 
          p: 2.5, 
          borderRadius: 4, 
          border: `2px dashed ${theme.palette.divider}`,
          textAlign: 'center',
          cursor: 'pointer',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          transition: 'all 0.3s ease',
          bgcolor: alpha(theme.palette.grey[100], 0.3),
          '&:hover': { 
            borderColor: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            transform: 'translateY(-2px)',
          }
        }}
        onClick={() => window.location.href = '/user'}
      >
        <ErrorOutlineIcon 
          sx={{ 
            fontSize: 48, 
            color: 'text.secondary', 
            mb: 1.5,
            mx: 'auto' 
          }} 
        />
        <Typography variant="h6" color="text.secondary" fontWeight={700} sx={{ fontSize: '1rem' }}>
          No Team Assignment
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, mb: 1.5, fontSize: '0.7rem' }}>
          You haven't been assigned to a team yet
        </Typography>
        <Chip 
          label="Manage Settings" 
          color="primary" 
          size="small"
          sx={{ width: 'fit-content', mx: 'auto', height: 26 }}
        />
      </Card>
    );
  }

  // Extract data using new field names from the backend
  const isTeamSource = spendingInfo.spending_source === 'team';

  const totalAvailable = parseFloat(
    spendingInfo.total_available_funds ||
    spendingInfo.team_available_spending ||
    0
  );

  const teamAllocated = parseFloat(
    spendingInfo.team_allocated_spending ||
    spendingInfo.effective_spending_limit ||
    0
  );

  const teamUsed = parseFloat(
    spendingInfo.team_used_spending ||
    0
  );

  const teamAvailable = parseFloat(
    spendingInfo.team_available_spending ||
    0
  );

  const companyBalance = parseFloat(spendingInfo.company_balance || 0);

  const companyLimit = parseFloat(
    spendingInfo.company_spending_limit ||
    0
  );

  // Check if team has actual spending power allocation
  const hasTeamAllocation = spendingInfo.has_team_allocation || teamAllocated > 0;

  const effectiveRate = spendingInfo.effective_interest_rate || '10';

  const hasNoFunds = !spendingInfo.has_available_funds || totalAvailable === 0;
  const hasLowFunds = totalAvailable > 0 && totalAvailable < 1000;
  const isHealthy = totalAvailable >= 1000;

  const utilizationPercent = teamAllocated > 0 
    ? (teamUsed / teamAllocated) * 100 
    : 0;

  const getUtilizationColor = (percentage) => {
    if (percentage >= 90) return theme.palette.error.main;
    if (percentage >= 75) return theme.palette.warning.main;
    if (percentage >= 50) return theme.palette.info.main;
    return theme.palette.success.main;
  };

  // Determine card styling - FIXED to use spending power
  const getCardStyle = () => {
    if (hasNoFunds) {
      return {
        background: `linear-gradient(135deg,
          ${alpha(theme.palette.info.main, 0.10)} 0%,
          ${alpha(theme.palette.info.light, 0.05)} 100%)`,
        border: `2px solid ${alpha(theme.palette.info.main, 0.25)}`,
        icon: <LockIcon sx={{ fontSize: 24 }} />,
        iconBg: alpha(theme.palette.info.main, 0.15),
        iconColor: theme.palette.info.main,
      };
    }
    
    if (hasLowFunds) {
      return {
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.warning.main, 0.12)} 0%, 
          ${alpha(theme.palette.warning.light, 0.06)} 100%)`,
        border: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
        icon: <TrendingUpIcon sx={{ fontSize: 24 }} />,
        iconBg: alpha(theme.palette.warning.main, 0.2),
        iconColor: theme.palette.warning.main,
      };
    }
    
    if (isTeamSource) {
      return {
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.main, 0.12)} 0%, 
          ${alpha(theme.palette.primary.light, 0.06)} 100%)`,
        border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        icon: <GroupIcon sx={{ fontSize: 24 }} />,
        iconBg: alpha(theme.palette.primary.main, 0.15),
        iconColor: theme.palette.primary.main,
      };
    }
    
    return {
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.success.main, 0.12)} 0%, 
        ${alpha(theme.palette.success.light, 0.06)} 100%)`,
      border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
      icon: <BusinessIcon sx={{ fontSize: 24 }} />,
      iconBg: alpha(theme.palette.success.main, 0.15),
      iconColor: theme.palette.success.main,
    };
  };

  const cardStyle = getCardStyle();

  // Show balance warning if negative
  const hasNegativeBalance = companyBalance < 0;

  return (
    <Card 
      elevation={0}
      sx={{ 
        borderRadius: 4,
        height: '100%',
        background: cardStyle.background,
        border: cardStyle.border,
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: 'visible',
        position: 'relative',
        "&:hover": { 
          transform: "translateY(-4px)",
          boxShadow: 6,
        }
      }}
      onClick={onClick}
    >
      {/* Status Badge */}
      {hasNoFunds && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            right: 16,
            zIndex: 1,
          }}
        >
          <Chip
            icon={<LockIcon sx={{ fontSize: 14 }} />}
            label="No Spending Power"
            color="info"
            size="small"
            sx={{ 
              fontWeight: 700,
              boxShadow: 2,
              height: 26,
              fontSize: '0.7rem'
            }}
          />
        </Box>
      )}

      {hasLowFunds && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            right: 16,
            zIndex: 1,
          }}
        >
          <Chip
            icon={<WarningIcon sx={{ fontSize: 14 }} />}
            label="Low Spending Power"
            color="warning"
            size="small"
            sx={{ 
              fontWeight: 700,
              boxShadow: 2,
              height: 26,
              fontSize: '0.7rem'
            }}
          />
        </Box>
      )}

      {isHealthy && isTeamSource && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            right: 16,
            zIndex: 1,
          }}
        >
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
            label="Team"
            color="primary"
            size="small"
            sx={{ 
              fontWeight: 700,
              boxShadow: 2,
              height: 26,
              fontSize: '0.7rem'
            }}
          />
        </Box>
      )}

      <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: cardStyle.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: `0 2px 8px ${alpha(cardStyle.iconColor, 0.2)}`,
              }}
            >
              {React.cloneElement(cardStyle.icon, { 
                sx: { ...cardStyle.icon.props.sx, fontSize: 24, color: cardStyle.iconColor } 
              })}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: '1rem',
                  mb: 0.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {isTeamSource 
                  ? spendingInfo.primary_team_name 
                  : spendingInfo.company_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ fontSize: '0.7rem' }}>
                {hasNoFunds 
                  ? 'No Spending Power' 
                  : isTeamSource 
                  ? 'Team Spending Power' 
                  : 'Company Spending Power'}
              </Typography>
            </Box>
          </Box>
          
          <Tooltip 
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  {isTeamSource ? 'Team Spending Power' : 'Company Spending Power'}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                  {isTeamSource 
                    ? `Your team has been allocated $${teamAllocated.toFixed(2)} for BlitzPay transactions.`
                    : `Your company has a $${companyLimit.toFixed(2)} spending limit for all BlitzPay transactions.`
                  }
                </Typography>
                {hasNegativeBalance && (
                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', mt: 0.5, color: 'warning.main' }}>
                    ⚠️ Company balance is ${companyBalance.toFixed(2)}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', mt: 0.5, opacity: 0.9 }}>
                  Click for transaction history →
                </Typography>
              </Box>
            }
            placement="top"
            arrow
          >
            <IconButton 
              size="small" 
              sx={{ 
                bgcolor: alpha(theme.palette.common.black, 0.05),
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                }
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Main Amount - FIXED to show spending power */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 800, 
                fontSize: '2rem',
                color: hasNoFunds 
                  ? theme.palette.info.main 
                  : hasLowFunds 
                  ? theme.palette.warning.main 
                  : theme.palette.text.primary
              }}
            >
              ${totalAvailable.toFixed(2)}
            </Typography>
            <Tooltip 
              title={
                <Box>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                    Available Spending Power
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                    This is your spending power limit minus any active BlitzPay advances.
                  </Typography>
                  {hasNegativeBalance && (
                    <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', mt: 0.5, color: 'warning.main' }}>
                      Note: Total funds including balance is ${totalAvailable.toFixed(2)}
                    </Typography>
                  )}
                </Box>
              }
              arrow
            >
              <HelpOutlineIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ fontSize: '0.7rem' }}>
            Available for BlitzPay
          </Typography>
        </Box>

        {/* Negative Balance Warning */}
        {hasNegativeBalance && !hasNoFunds && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2,
              py: 0.75,
              px: 1.5,
              borderRadius: 1.5,
              '& .MuiAlert-icon': { fontSize: 18, py: 0 },
              '& .MuiAlert-message': { width: '100%', py: 0 }
            }}
          >
            <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.7rem' }}>
              Company Balance: ${companyBalance.toFixed(2)}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', mt: 0.25 }}>
              Total available funds: ${totalAvailable.toFixed(2)}
            </Typography>
          </Alert>
        )}

        {/* Utilization Bar (only when team has spending power allocation OR when company direct) */}
        {(hasTeamAllocation || !isTeamSource) && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.7rem' }}>
                  Utilization
                </Typography>
                <Tooltip 
                  title={
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      {isTeamSource
                        ? `Shows how much of your team's $${teamAllocated.toFixed(2)} allocation has been used.`
                        : `Shows how much of the company's $${companyLimit.toFixed(2)} limit has been used.`
                      }
                    </Typography>
                  }
                  arrow
                >
                  <HelpOutlineIcon sx={{ fontSize: 12, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem' }}>
                {utilizationPercent.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(utilizationPercent, 100)}
              sx={{ 
                height: 6, 
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.grey[300], 0.3),
                "& .MuiLinearProgress-bar": {
                  backgroundColor: getUtilizationColor(utilizationPercent),
                  borderRadius: 3,
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
              <Tooltip 
                title={
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    Total amount already spent on BlitzPay transactions
                  </Typography>
                }
                arrow
              >
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', cursor: 'help' }}>
                  ${teamUsed.toFixed(0)} used
                </Typography>
              </Tooltip>
              <Tooltip 
                title={
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    Remaining amount you can spend on BlitzPay
                  </Typography>
                }
                arrow
              >
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'success.main', fontWeight: 600, cursor: 'help' }}>
                  ${teamAvailable.toFixed(0)} left
                </Typography>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* No Spending Power Alert */}
        {hasNoFunds && (
          <Alert 
            severity="info" 
            sx={{ 
              py: 0.75,
              px: 1.5,
              borderRadius: 1.5,
              '& .MuiAlert-icon': {
                fontSize: 18,
                py: 0
              },
              '& .MuiAlert-message': {
                width: '100%',
                py: 0
              }
            }}
          >
            <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.7rem' }}>
              {isTeamSource 
                ? 'Your team has no allocated spending power' 
                : 'Company has no spending power available'}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', mt: 0.25, opacity: 0.9 }}>
              Contact supervisor for allocation
            </Typography>
          </Alert>
        )}

        {/* Footer - Discount Rate & History */}
        {!hasNoFunds && (
          <Box 
            sx={{ 
              mt: 'auto', 
              pt: 1.5, 
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.7rem' }}>
                  Discount Rate
                </Typography>
                <Tooltip 
                  title={
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      The interest rate charged when using BlitzPay for instant payments
                    </Typography>
                  }
                  arrow
                >
                  <HelpOutlineIcon sx={{ fontSize: 12, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Typography variant="caption" color="success.main" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
                {effectiveRate}%
              </Typography>
            </Box>
            
            {/* Transaction History Hint */}
            <Chip 
              icon={<ReceiptIcon sx={{ fontSize: 14 }} />}
              label="View Transaction History" 
              size="small"
              color="primary"
              variant="outlined"
              sx={{ 
                width: '100%',
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 600,
                borderStyle: 'dashed',
                '&:hover': {
                  borderStyle: 'solid',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                }
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamSpendingCard;